import React, { useState } from "react";
import { toast } from "sonner";
import { KeyRound, MessageCircle, Sparkles, Check, Pencil, Eye, Loader2, ShieldCheck, ShieldAlert, Receipt, LogOut, Coffee, IdCard } from "lucide-react";
import { ensurePin, fetchPinWa, markReservation, sendFidelity, setGuestPhone, openLater } from "../../lib/checkinApi";
import { BreakfastDialog } from "./BreakfastDialog";
import { DocsDialog } from "./DocsDialog";

const fmt = (d) => (d ? d.slice(5).split("-").reverse().join("/") : "");
const errMsg = (e) => e?.response?.data?.detail || e?.message || "Errore";

// One guest (arrival / departure / in-house) with the big touch actions for the desk tablet.
export function GuestCard({ item, kind, onChanged, onShow }) {
  const [busy, setBusy] = useState("");
  const [editPhone, setEditPhone] = useState(false);
  const [phone, setPhone] = useState(item.phone || "");
  const [breakfast, setBreakfast] = useState(false);
  const [docs, setDocs] = useState(false);
  const bfList = item.breakfast_vouchers || [];
  const bfAny = bfList.length > 0;
  const bfSent = bfList.some((v) => v.sent_guest_at);
  const done = kind === "departure" ? !!item.checked_out_at : !!item.checked_in_at;

  const run = async (key, fn, okMsg) => {
    setBusy(key);
    try { const r = await fn(); if (okMsg) toast.success(typeof okMsg === "function" ? okMsg(r) : okMsg); onChanged(); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(""); }
  };

  const savePhone = () => run("phone", () => setGuestPhone(item.id, phone.trim()).then(() => setEditPhone(false)), "Telefono salvato");

  return (
    <article className={`rounded-3xl border bg-white shadow-sm overflow-hidden ${done ? "border-emerald-200" : "border-[#e8dcc8]"}`} data-testid={`checkin-card-${item.id}`}>
      <header className="flex items-center gap-4 px-5 pt-5">
        <div className="h-16 w-16 rounded-2xl bg-[#4a1c17] flex items-center justify-center shrink-0 overflow-hidden">
          {item.room.logo ? <img src={item.room.logo} alt={item.room.name} className="h-12 w-12 object-contain" /> : <span className="text-[#f7ebdf] font-bold text-xl">{item.room.number}</span>}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-xs uppercase tracking-widest text-[#7c5a4e]">Camera {item.room.name}</div>
          <h3 className="font-serif text-xl sm:text-2xl font-bold text-[#2b1410] truncate" data-testid={`checkin-guest-${item.id}`}>{item.guest}</h3>
          <div className="text-sm text-[#7c5a4e]">{fmt(item.start)} → {fmt(item.end)} · {item.nights} {item.nights === 1 ? "notte" : "notti"}</div>
        </div>
        {done && <span className="rounded-full bg-emerald-100 text-emerald-800 px-3 py-1 text-xs font-bold inline-flex items-center gap-1" data-testid={`checkin-done-${item.id}`}><Check className="h-3.5 w-3.5" /> {kind === "departure" ? "Check-out" : "Check-in"}</span>}
      </header>

      <div className="px-5 pt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
        {editPhone ? (
          <div className="flex items-center gap-2">
            <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+39 …" inputMode="tel" className="h-10 rounded-full border border-[#e8dcc8] px-4 text-base" data-testid={`checkin-phone-input-${item.id}`} />
            <button onClick={savePhone} className="h-10 rounded-full bg-[#4a1c17] text-white px-4 text-sm font-semibold" data-testid={`checkin-phone-save-${item.id}`}>{busy === "phone" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salva"}</button>
          </div>
        ) : (
          <button onClick={() => setEditPhone(true)} className="inline-flex items-center gap-1.5 text-[#2b1410] hover:underline" data-testid={`checkin-phone-${item.id}`}>
            <Pencil className="h-3.5 w-3.5 text-[#C48B31]" /> {item.phone || <span className="text-rose-700 font-semibold">telefono mancante</span>}
          </button>
        )}
        <PinChip item={item} />
        {item.fidelity_member && <span className="inline-flex items-center gap-1 rounded-full bg-[#fdf6e9] border border-[#C48B31]/50 px-2.5 py-0.5 text-xs font-semibold text-[#4a1c17]" data-testid={`checkin-fidelity-badge-${item.id}`}><Sparkles className="h-3 w-3 text-[#C48B31]" /> Fidelity</span>}
        {item.wa_sent_at && <span className="inline-flex items-center gap-1 rounded-full bg-purple-50 px-2.5 py-0.5 text-xs font-semibold text-purple-700"><MessageCircle className="h-3 w-3" /> codice inviato</span>}
      </div>

      <div className={`p-4 lg:p-3 xl:p-5 grid gap-2.5 xl:gap-3 ${kind === "arrival" ? "grid-cols-2 2xl:grid-cols-3" : "grid-cols-2"}`}>
        {kind !== "departure" && (
          <>
            <Btn testId={`checkin-pin-btn-${item.id}`} tone="gold" busy={busy === "pin"} icon={KeyRound} label={item.pin ? "Verifica codice" : "Genera codice"}
              onClick={() => run("pin", () => ensurePin(item.id), (r) => ({ created_ondemand: "Codice creato sulla serratura", existed: "Codice verificato sulla serratura", reconciled: "Codice riallineato alla serratura", unverified: "Serratura non raggiungibile: codice dal database" }[r.pin_action] || "Codice pronto"))} />
            <Btn testId={`checkin-show-btn-${item.id}`} tone="bordeaux" icon={Eye} label="Mostra" disabled={!item.pin} onClick={() => onShow(item)} />
            <Btn testId={`checkin-wa-btn-${item.id}`} tone={item.wa_sent_at ? "purple" : "green"} busy={busy === "wa"} icon={MessageCircle} label={item.wa_sent_at ? "WhatsApp ✓" : "WhatsApp"}
              onClick={() => { setBusy("wa"); openLater(() => fetchPinWa(item.id).then((r) => r.wa_url)).then(() => { toast.success("WhatsApp aperto"); onChanged(); }).catch((e) => toast.error(errMsg(e))).finally(() => setBusy("")); }} />
            {!item.fidelity_member && (
              <Btn testId={`checkin-fidelity-btn-${item.id}`} tone={item.fidelity_sent_at ? "purple" : "goldOutline"} busy={busy === "fid"} icon={Sparkles} label={item.fidelity_sent_at ? "Fidelity ✓" : "Fidelity +10 €"} disabled={!item.phone}
                onClick={() => { setBusy("fid"); openLater(() => sendFidelity(item.id).then((r) => r.wa_link)).then(() => { toast.success("Tessera Fidelity pronta · 10 € di benvenuto"); onChanged(); }).catch((e) => toast.error(errMsg(e))).finally(() => setBusy("")); }} />
            )}
          </>
        )}
        {kind === "departure" && (
          <a href={`/admin/pulizie/reception?room_id=${item.room.id}&from=${item.start}&to=${item.end}`} className={`h-14 rounded-2xl text-white inline-flex items-center justify-center gap-2 text-sm font-bold ${item.frigobar_wa_sent_at ? "bg-purple-600" : "bg-[#4a1c17]"}`} data-testid={`checkin-frigobar-${item.id}`}>
            <Receipt className="h-5 w-5" /> {item.frigobar_wa_sent_at ? "Conto frigobar inviato ✓" : "Conto frigobar"}
          </a>
        )}
        {kind !== "departure" && <Btn testId={`checkin-docs-btn-${item.id}`} tone="cream" icon={IdCard} label="Documenti" onClick={() => setDocs(true)} />}
        <Btn testId={`checkin-breakfast-btn-${item.id}`} tone={bfSent ? "purple" : bfAny ? "amberOutline" : "cream"} icon={Coffee} label={bfSent ? "Colazione ✓" : bfAny ? "Colazione da inviare" : "Colazione al bar"} onClick={() => setBreakfast(true)} />
        <Btn testId={`checkin-mark-btn-${item.id}`} tone={done ? "doneOutline" : "emerald"} busy={busy === "mark"} icon={kind === "departure" ? LogOut : Check}
          label={kind === "departure" ? (done ? "Annulla check-out" : "Check-out fatto") : (done ? "Annulla check-in" : "Check-in fatto")}
          onClick={() => run("mark", () => markReservation(item.id, kind === "departure" ? { checked_out: !done } : { checked_in: !done }))} />
      </div>
      {breakfast && <BreakfastDialog item={item} onClose={() => setBreakfast(false)} onCreated={onChanged} />}
      {docs && <DocsDialog item={item} onClose={() => setDocs(false)} />}
    </article>
  );
}

function PinChip({ item }) {
  if (!item.pin) return <span className="inline-flex items-center gap-1 rounded-full bg-rose-50 px-2.5 py-0.5 text-xs font-semibold text-rose-700" data-testid={`checkin-pin-state-${item.id}`}><ShieldAlert className="h-3 w-3" /> codice da creare</span>;
  const ok = item.pin_state === "verified";
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold ${ok ? "bg-emerald-50 text-emerald-800" : "bg-amber-50 text-amber-800"}`} data-testid={`checkin-pin-state-${item.id}`}>
      {ok ? <ShieldCheck className="h-3 w-3" /> : <ShieldAlert className="h-3 w-3" />}
      <span className="font-mono text-sm tracking-widest">{item.pin}</span>
      {ok ? "verificato" : "da verificare"}
    </span>
  );
}

const TONES = {
  gold: "bg-[#C48B31] hover:bg-[#a9741f] text-white",
  goldOutline: "bg-white border-2 border-[#C48B31] text-[#4a1c17] hover:bg-[#fdf6e9]",
  bordeaux: "bg-[#4a1c17] hover:bg-[#2b1410] text-white",
  green: "bg-[#25D366] hover:bg-[#1ebe5b] text-white",
  emerald: "bg-emerald-600 hover:bg-emerald-700 text-white",
  doneOutline: "bg-white border-2 border-emerald-300 text-emerald-800 hover:bg-emerald-50",
  cream: "bg-[#fdf6e9] border-2 border-[#e8dcc8] text-[#4a1c17] hover:bg-[#f7ebdf]",
  amberOutline: "bg-amber-50 border-2 border-amber-400 text-amber-900 hover:bg-amber-100",
  purple: "bg-purple-600 text-white hover:bg-purple-700",
};

function Btn({ icon: Icon, label, tone, onClick, busy, disabled, testId }) {
  return (
    <button onClick={onClick} disabled={disabled || busy} data-testid={testId}
      className={`h-14 rounded-2xl inline-flex items-center justify-center gap-2 px-3 text-sm font-bold shadow-sm active:scale-[0.98] transition-[transform,background-color] disabled:opacity-40 disabled:cursor-not-allowed ${TONES[tone]}`}>
      {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Icon className="h-5 w-5" />} <span className="truncate">{label}</span>
    </button>
  );
}
